import Highcharts, { animate, SeriesOptionsType } from "highcharts";
import "highcharts/highcharts-more";
//import "highcharts/modules/no-data-to-display";
import "highcharts/modules/datagrouping";
import { getColorByValueAndType, getColorByValueAndTypeFading } from "../../utils/colors";
import { DataSample, DataSampleType, DataSampleUnit } from "../../types";
import { getWindDirectionText } from "../../utils/wind";
import { formatNumbers, dateToMs, getUnitString, getHourText } from "../../utils";

Highcharts.setOptions({
	time: {
		timezone: "Europe/Oslo"
	},

});
export function toHighchartsXY(samples: DataSample[] | null, getValue: (s: DataSample) => number | null | undefined = s => s.Value): Array<XY> | null {
	if (samples === null) {
		return null;
	}
	const xy = samples
		.map((s) => {
			const x = dateToMs(s.Timestamp);
			const y = getValue(s);
			return y == null || Number.isNaN(y) ? null : ([x, Number(y)] as XY);
		})
		.filter((p): p is XY => !!p)
		.sort((a, b) => a[0] - b[0]);
	const byTs = new Map<number, number>();
	for (const [x, y] of xy) byTs.set(x, y);

	return [...byTs].sort((a, b) => a[0] - b[0]);
}

function fitPolarPane(chart: Highcharts.Chart, options?: { distance?: number; padding?: number }) {
	const xAxis = chart.xAxis[0];
	if (!xAxis) {
		return;
	}
	const distance = options?.distance ?? 10;
	const padding = options?.padding ?? 6;

	let maxWidth = 0, maxHeight = 0;
	const ticks = (xAxis).ticks || {};
	for (const key in ticks) {
		const label = ticks[key]?.label as Highcharts.SVGElement | undefined;
		if (label) {
			const boundingBox = label.getBBox();
			maxWidth = Math.max(maxWidth, boundingBox.width);
			maxHeight = Math.max(maxHeight, boundingBox.height);
		}
	}
	const plotWidth = chart.plotWidth;
	const plotHeight = chart.plotHeight;

	const paddingX = (distance >= 0 ? (maxWidth + distance) : 0) + padding;
	const paddingY = (distance >= 0 ? (maxHeight + distance) : 0) + padding;

	const radius = Math.max(10, Math.min((plotWidth / 2) - paddingX, (plotHeight / 2) - paddingY));
	const newSize = Math.max(0, Math.floor(radius * 2));
	const pane = chart.options.pane || {};
	const current = (pane as any).computedSizePx;
	if (current !== newSize) {
		(pane as any).computedSizePx = newSize;
		chart.update({
			pane: { size: newSize, center: ["50%", "50%"] }
		}, false);
		chart.redraw(false);
	}
}

export function lockXAxis(chart: Highcharts.Chart, from: number, to?: number) {
	const min = from;
	const max = to ?? new Date(Date.now()).getTime();
	chart.update({
		xAxis: [{
			type: 'datetime',
			min, max,
			startOnTick: false,
			endOnTick: false,
			minPadding: 0,
			maxPadding: 0
		}]
	}, false);
	chart.redraw();
}

type WindRosePoint =
	{
		date: Date,
		x: number,
		y: number,
		type: DataSampleType,
		color: string
	};
export type XY = [number, number];

export const speedAndDirectionChart = {
	speedSeries: function (isSecondary: boolean, title: string | undefined, data: DataSample[] | null, unit: DataSampleUnit): SeriesOptionsType {
		const dataSeries = toHighchartsXY(data) ?? [];
		return {
			name: title,
			type: "spline",
			tooltip: {
				valueSuffix: getUnitString(unit),
				valueDecimals: 2,
			},
			dataGrouping: {
				enabled: true,
				groupPixelWidth: 5
			},
			colorIndex: isSecondary ? 3 : 0,
			data: dataSeries
		}
	},
	directionSeries: function (isSecondary: boolean, title: string | undefined, data: DataSample[] | null, unit: DataSampleUnit): SeriesOptionsType {
		const dataSeries = toHighchartsXY(data) ?? [];
		return {
			type: "spline",
			name: title,
			yAxis: 1,
			tooltip: {
				valueSuffix: getUnitString(unit),
				valueDecimals: 0
			},
			colorIndex: isSecondary ? 5 : 2,
			dataGrouping: {
				enabled: true,
				groupPixelWidth: 5
			},
			data: dataSeries
		};
	},
	create: function (options: { containerId: string, historyChartIntervalHours: number }, sampleType: "Wind" | "Current", yAxis1Unit: DataSampleUnit, yAxis2Unit: DataSampleUnit, from?: number, to?: number) {
		return new Highcharts.Chart({
			chart: {
				renderTo: options.containerId,
				backgroundColor: "transparent"
			},
			credits: {
				enabled: false,
			},
			exporting: {
				enabled: false,
			},
			title: {
				text: `${sampleType} last ${options.historyChartIntervalHours === 1 ? "hour" : `${options.historyChartIntervalHours} hours`}`,
				x: -20, //center
			},
			xAxis: {
				type: 'datetime',
				gridLineColor: '#666666',
				tickInterval: (options.historyChartIntervalHours * 3600 * 1000) / 10,
				tickWidth: 0,
				gridLineWidth: 1,
				labels: {
					style: {
						color: '#f8f9fa'
					}
				}
			},
			yAxis: [
				{
					softMax: 5,
					min: 0,
					gridLineColor: '#666666',
					title: {
						text: '',
						style: {
							color: '#f8f9fa'
						}
					},
					labels: {
						format: `{value} ${getUnitString(yAxis1Unit)}`,
						style: {
							color: '#f8f9fa'
						}
					},
					plotLines: [
						{
							value: 0,
							width: 1
						},
					],
				},
				{
					min: 0,
					max: 360,
					showLastLabel: false,
					gridLineColor: '#666666',
					startOnTick: false,
					endOnTick: false,
					title: {
						text: '',
						style: {
							color: '#f8f9fa'
						}
					},
					labels: {
						format: `{value} ${getUnitString(yAxis2Unit)}`,
						style: {
							color: '#f8f9fa'
						}
					},
					opposite: true,
				},
			],
			tooltip: {
				enabled: true,
				shared: true,
				headerFormat: '<small>{point.x:%d.%m.%Y %H:%M:%S}</small><br>',
			},
			legend: {
				itemStyle: {
					color: '#f8f9fa'
				},
				itemHoverStyle: {
					color: '#666666'
				},
			},
			plotOptions: {
				spline: {
					lineWidth: 2,
					states: {
						hover: {
							lineWidth: 2,
						},
					},
					marker: {
						enabled: false,
					}
				},
			},
			series: [],
		});
	},
};


export const roseChart = {
	addPointWithAnimation: function (chart: Highcharts.Chart, rosePoint: WindRosePoint, shift = false) {
		if (!chart) {
			return;
		}
		chart.series[0].addPoint(rosePoint, true, shift);
		const point = chart.series[0].data[chart.series[0].data.length - 1];
		if (!point) {
			return;
		}
		console.log(chart.series[0].points);
		const maxSize = 20;
		const originalSize = 3;
		const timeToAnimate = 300;
		point.update({
			marker: {
				radius: maxSize
			}
		}, true);

		setTimeout(() => {
			point.update({
				marker: {
					radius: originalSize
				}
			});
		}, timeToAnimate);
	},
	createRosePoint: function (dir: DataSample, speed: DataSample, maxAge: number): WindRosePoint {
		var date = speed.Timestamp;
		return {
			date: date,
			x: dir.Value,
			y: parseFloat(speed.Value.toFixed(2)),
			type: speed.Type,
			color: getColorByValueAndTypeFading(speed.Value, speed.Type, speed.Unit, speed.Timestamp, maxAge*60)
		};
	},
	create: function (options: { containerId: string, data: WindRosePoint[], maxAgeHours: number, turboThreshold: number }, sampleType: "Wind" | "Current", speedUnit: DataSampleUnit, directionUnit: DataSampleUnit) {
		return new Highcharts.Chart({
			chart: {
				renderTo: options.containerId,
				polar: true,
				reflow: true,
				type: 'scatter',
				borderColor: '#f8f9fa',
				backgroundColor: "transparent",
				zooming: {
					type: undefined,
					pinchType: undefined,
					mouseWheel: { enabled: false }
				},
				panning: { enabled: false },
				events: {
					load: function () { fitPolarPane(this as Highcharts.Chart); },
					render: function (t) {
						console.log(t);
						const chart = this as Highcharts.Chart;
						const dataSampleType = sampleType == "Wind" ? DataSampleType.WIND_SPEED : DataSampleType.CURRENT_SPEED;
						const series = chart.series[0];
						if (!series) {
							return;
						}
						if ((chart as any).__colorPass) {
							return;
						}
						(chart as any).__colorPass = true;

						let needsRedraw = false;
						const axis = chart.yAxis[0];
						if (axis) {
							const ticks = Object.values(axis.ticks || {}).filter(t => t && typeof t.pos === "number").sort((a, b) => a.pos - b.pos);
							ticks.forEach((tick, idx) => {
								const gridLine = tick.gridLine;
								if (!gridLine) {
									return;
								}
								const color = getColorByValueAndType(tick.pos, dataSampleType, speedUnit);
								gridLine.attr({
									stroke: color
								});
							});
						}
						series.points.forEach((point: Highcharts.Point) => {
							const date = (point as any).date;
							const newColor = getColorByValueAndTypeFading(point.y ?? 0, dataSampleType,speedUnit, new Date(date), options.maxAgeHours * 60);

							if (point.color !== newColor) {
								point.update({ color: newColor }, false);
								needsRedraw = true;
							}
						});
						if (needsRedraw) {
							this.redraw();
						}
						(chart as any).__colorPass = false;
						fitPolarPane(chart)
					},
				},
			},
			title: {
				text: `${sampleType} ${getHourText(options.maxAgeHours).toLowerCase()}`,
			},
			legend: {
				enabled: false
			},
			xAxis: {
				min: 0,
				max: 360,
				lineColor: '#666666',
				gridLineColor: '#666666',
				tickInterval: 22.5,
				startOnTick: false,
				endOnTick: false,
				tickmarkPlacement: 'on',
				gridLineInterpolation: "polygon",
				type: "linear",
				labels: {
					formatter: function () {
						return getWindDirectionText(this.value as number, true);
					},
					allowOverlap: true,
					reserveSpace: true,
					distance: 10,
					style: {
						color: '#f8f9fa'
					}
				}
			},
			yAxis: {
				crosshair: true,
				min: 0,
				softMax: 20,
				tickAmount: 4,
				showLastLabel: true,
				gridLineColor: '#666666',
				gridLineWidth: 2,
				title: {
					text: '',
					style: {
						color: '#f8f9fa',
					}
				},
				labels: {
					formatter: function () {
						const dataSampleType = sampleType == "Wind" ? DataSampleType.WIND_SPEED : DataSampleType.CURRENT_SPEED;
						const speedColor = getColorByValueAndType(Number(this.pos), dataSampleType, speedUnit);
						return `<span style="color: ${speedColor}">${this.pos}${speedUnit}</span>`;
					},
					style: {
						color: '#f8f9fa',
						fontWeight: "900",
					},

				},
				reversedStacks: false,
			},
			tooltip: {
				shared: false,
				useHTML: true,
				followTouchMove: false,
				formatter: function () {
					const date = (this as any).date;
					const timestamp = Highcharts.dateFormat('%d.%m.%Y %H:%M:%S', +new Date(date))
					const windDeg = formatNumbers(this.x, 0);

					return `<small>${timestamp}</small><br>
							Wind speed: <b>${this.y}${getUnitString(speedUnit)}</b><br>
							Wind direction: <b>${windDeg}${getUnitString(directionUnit) } (${getWindDirectionText(Number(this.x), true)})</b>`;
				},
			},
			series: [{
				type: "scatter",
				data: options.data,
				animation: { duration: 10 }
			}],

			plotOptions: {
				series: {
					turboThreshold: options.turboThreshold,
					pointPlacement: undefined,
					marker: {
						radius: 3,
					},
					point: {
						events: {
							click: function () {
								console.log("Clicked on point!", this);
							}
						}
					}
				},
			},
			exporting: { enabled: false },
			credits: {
				enabled: false,
			},
			responsive: {
				rules: [{
					condition: { maxWidth: 420 },
					chartOptions: {
						xAxis: { labels: { distance: 10, style: { fontSize: '10px' } } }
					}
				}]
			}
		});
	},
}